import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Snackbar,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

const Pagos = () => {
    const { usuario, token } = useAuth();

    const [pagos, setPagos] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [mensaje, setMensaje] = useState("");

    const [pagoSel, setPagoSel] = useState(null);
    const [bizumOpen, setBizumOpen] = useState(false);

    // justificante + IBAN
    const [file, setFile] = useState(null);
    const [usarIbanGuardado, setUsarIbanGuardado] = useState(true);
    const [ibanManual, setIbanManual] = useState("");
    const [uploading, setUploading] = useState(false);

    // perfil (solo IBAN)
    const [perfilIban, setPerfilIban] = useState("");

    const API = process.env.REACT_APP_API_URL;
    const headers = { Authorization: `Bearer ${token}` };

    // ===================
    // Cargar pagos
    // ===================
    const obtenerPagos = async () => {
        try {
            setCargando(true);
            const res = await axios.get(`${API}/api/pagos/colegiado/${usuario.codigo}`, {
                headers,
            });
            const mapeados = (res.data || []).map((p) => ({
                id: p.id,
                concepto: p.concepto || "-",
                importe: p.importe != null ? p.importe.toFixed(2) : "-",
                fechaEmision: p.fechaEmision || p.fecha_emision || "-",
                fechaVencimiento: p.fechaVencimiento || p.fecha_vencimiento || "-",
                estado: (p.estado || "").toUpperCase(), // viene de inscripción en tu backend
                tipoPago: p.tipoPago?.nombre || p.tipo_pago?.nombre || "-",
            }));
            setPagos(mapeados);
        } catch (error) {
            console.error("Error al obtener pagos:", error);
            setMensaje("Error al cargar pagos");
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        obtenerPagos();
    }, []);

    // ===================
    // Cargar perfil (IBAN) al abrir el modal
    // ===================
    const cargarPerfilIban = async () => {
        try {
            const res = await axios.get(`${API}/colegiado/perfil`, { headers });
            const c = res?.data || {};
            const ibanPerfil =
                (c.iban ?? c.IBAN ?? c.cuenta_Bancaria ?? c.cuenta_bancaria ?? "").toString().trim();
            setPerfilIban(ibanPerfil);

            if (ibanPerfil) {
                setUsarIbanGuardado(true);
                setIbanManual("");
            } else {
                setUsarIbanGuardado(false);
                setIbanManual("");
            }
        } catch (e) {
            console.error("Error cargando perfil:", e);
            setPerfilIban("");
            setUsarIbanGuardado(false);
        }
    };

    const abrirBizum = async (row) => {
        setPagoSel(row);
        setFile(null);
        setIbanManual("");
        setUploading(false);
        await cargarPerfilIban();
        setBizumOpen(true);
    };

    // ===================
    // Confirmar (subir justificante + IBAN + marcar pagada)
    // ===================
    const handlePago = async () => {
        if (!pagoSel) return;

        // validaciones mínimas
        if (!file) {
            setMensaje("Adjunta el justificante antes de confirmar.");
            return;
        }
        if (usarIbanGuardado && !perfilIban) {
            setMensaje("No hay IBAN guardado en tu perfil. Introduce un IBAN manual.");
            return;
        }
        if (!usarIbanGuardado && !ibanManual.trim()) {
            setMensaje("Introduce un IBAN o marca 'usar IBAN guardado'.");
            return;
        }

        try {
            setUploading(true);

            // 1) subir justificante
            const fd = new FormData();
            fd.append("file", file);
            fd.append("colegiadoId", String(usuario.codigo));
            fd.append("pagoId", String(pagoSel.id)); // por si tu backend lo usa

            await axios.post(`${API}/api/documentos/subir`, fd, {
                headers: { ...headers, "Content-Type": "multipart/form-data" },
            });

            // 2) guardar IBAN del pago (mandamos siempre el IBAN explícito)
            const ibanAEnviar = usarIbanGuardado ? perfilIban : ibanManual.trim();
            const bodyIban = { usarIbanColegiado: false, iban: ibanAEnviar };

            await axios.put(`${API}/api/pagos/${pagoSel.id}/iban`, bodyIban, { headers });

            // 3) marcar como PAGADA (si backend lo permite al colegiado)
            await axios.put(
                `${API}/api/pagos/${pagoSel.id}`,
                { ...pagoSel, estado: "PAGADA" },
                { headers }
            );

            setMensaje("✅ Justificante subido e IBAN guardado. Pago realizado correctamente.");
            setBizumOpen(false);
            setPagoSel(null);
            setFile(null);
            setIbanManual("");
            setUsarIbanGuardado(true);
            obtenerPagos();
        } catch (error) {
            console.error("Error al pagar:", error);
            const msg =
                error?.response?.data && typeof error.response.data === "string"
                    ? error.response.data
                    : error?.response?.status === 403
                        ? "Acceso denegado. Revisa las reglas de autorización del backend."
                        : "❌ Error al procesar el pago";
            setMensaje(msg);
        } finally {
            setUploading(false);
        }
    };

    const columnas = [
        { field: "id", headerName: "ID", width: 80 },
        { field: "concepto", headerName: "Concepto", width: 260 },
        { field: "importe", headerName: "Importe (€)", width: 120 },
        { field: "fechaEmision", headerName: "F. Emisión", width: 140 },
        { field: "fechaVencimiento", headerName: "F. Vencimiento", width: 160 },
        {
            field: "estado",
            headerName: "Estado",
            width: 140,
            renderCell: (params) => (
                <Chip
                    size="small"
                    label={params.value}
                    color={
                        params.value === "PAGADA"
                            ? "success"
                            : params.value === "RECHAZADA"
                                ? "error"
                                : "warning"
                    }
                />
            ),
        },
        { field: "tipoPago", headerName: "Tipo de pago", width: 160 },
        {
            field: "acciones",
            headerName: "Acciones",
            width: 150,
            renderCell: (params) =>
                params.row.estado === "PENDIENTE" ? (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={() => abrirBizum(params.row)}
                    >
                        Pagar
                    </Button>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        —
                    </Typography>
                ),
        },
    ];

    return (
        <Box
            sx={{
                backgroundColor: "white",
                padding: 4,
                borderRadius: 2,
                boxShadow: 3,
                margin: 2,
            }}
        >
            <Typography variant="h5" gutterBottom>
                Mis Pagos
            </Typography>

            {cargando ? (
                <CircularProgress />
            ) : pagos.length === 0 ? (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                    No tienes pagos registrados.
                </Typography>
            ) : (
                <div style={{ height: 500, width: "100%" }}>
                    <DataGrid
                        rows={pagos}
                        columns={columnas}
                        pageSize={10}
                        rowsPerPageOptions={[5, 10, 20]}
                        disableSelectionOnClick
                    />
                </div>
            )}

            {/* Modal Bizum */}
            <Dialog open={bizumOpen} onClose={() => setBizumOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Adjuntar Justificante de pago</DialogTitle>
                <DialogContent dividers>
                    <Typography>
                        Adjunte el justificante de <strong>{pagoSel?.importe} €</strong> por <strong>{pagoSel?.concepto}</strong>.
                    </Typography>

                    {/* justificante */}
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                        Justificante de pago (1 archivo)
                    </Typography>
                    <input
                        type="file"
                        accept="application/pdf,image/*"
                        onChange={(e) => setFile((e.target.files || [])[0] || null)}
                    />
                    {file && (
                        <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                            {file.name} — {(file.size / 1024).toFixed(0)} KB
                        </Typography>
                    )}

                    {/* IBAN */}
                    <FormControlLabel
                        sx={{ mt: 2 }}
                        control={
                            <Checkbox
                                checked={usarIbanGuardado}
                                onChange={(e) => setUsarIbanGuardado(e.target.checked)}
                            />
                        }
                        label={
                            perfilIban
                                ? `Usar IBAN guardado (${perfilIban})`
                                : "Usar IBAN guardado (no hay IBAN guardado)"
                        }
                    />

                    {!usarIbanGuardado && (
                        <TextField
                            label="IBAN (si es distinto)"
                            value={ibanManual}
                            onChange={(e) => setIbanManual(e.target.value)}
                            placeholder="ES00 0000 0000 00 0000000000"
                            fullWidth
                            sx={{ mt: 1 }}
                        />
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        El IBAN es necesario en caso de devolución.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Si el IBAN es distinto del habitual, desmarque "Usar IBAN guardado" e introduzcalo.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBizumOpen(false)} disabled={uploading}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handlePago}
                        variant="contained"
                        color="primary"
                        // 👇 se deshabilita si no hay archivo adjunto o si está subiendo
                        disabled={uploading || !file}
                    >
                        {uploading ? "Procesando..." : "Confirmar pago"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!mensaje}
                autoHideDuration={4000}
                onClose={() => setMensaje("")}
                message={mensaje}
            />
        </Box>
    );
};

export default Pagos;
